// maths.portll.net — Task detail (plan §1.5).
//
// The parent's briefing for one Home Mission. It reads warm and human — a hook and
// what's needed, WHY it matters (with the ACARA codes tucked behind an expandable
// tag), the say-it-aloud steps, the kid prompt as a distinct callout, the "little
// wins" you'll notice, any safety note, then the actions: Print sheet, Mark planned
// (a private, on-device note), and the big "We did it" that opens the completion flow.
//
// Task loading mirrors lib/tasks: prefer the published list for the child's year, and
// fall back to a direct getDoc(tasks/{taskId}) so a preview/edge task still resolves.
// If a task is `comingSoon`, the whole action row is replaced by a friendly panel.

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase.js";
import { fetchPublishedTasksForYear } from "../lib/tasks.js";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import {
  PageHeader,
  Card,
  Chip,
  Button,
  Note,
  Spinner,
  EmptyState,
  StrandBadge,
  CurriculumTag,
  injectStyles,
} from "../components/ui.jsx";

const SETTING_META = {
  indoor_table: { label: "At a table", emoji: "🪑" },
  kitchen: { label: "In the kitchen", emoji: "🍳" },
  outdoor: { label: "Outdoors", emoji: "🌳" },
  neighbourhood: { label: "Around the neighbourhood", emoji: "🏘️" },
  anywhere: { label: "Anywhere", emoji: "✨" },
};

const plannedKey = (childId, taskId) => `maths.planned.${childId}.${taskId}`;

export default function TaskDetail() {
  const { childId, taskId } = useParams();
  const { profiles, displayNameFor, loading: profilesLoading } = useProfiles();

  const child = useMemo(
    () => profiles.find((p) => p.id === childId) || null,
    [profiles, childId]
  );

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [planned, setPlanned] = useState(false);

  const base = `/grown-ups/child/${childId}/task/${taskId}`;

  // Reflect the on-device "planned" note (a private nudge, never synced/uploaded).
  useEffect(() => {
    try {
      setPlanned(localStorage.getItem(plannedKey(childId, taskId)) === "1");
    } catch {
      setPlanned(false);
    }
  }, [childId, taskId]);

  useEffect(() => {
    if (profilesLoading) return; // wait for the child's year before choosing a loader
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        let found = null;
        if (child && child.yearLevel != null) {
          const list = await fetchPublishedTasksForYear(child.yearLevel);
          found = list.find((t) => t.id === taskId) || null;
        }
        if (!found) {
          const snap = await getDoc(doc(db, `tasks/${taskId}`));
          if (snap.exists()) found = { id: snap.id, ...snap.data() };
        }
        if (!alive) return;
        setTask(found);
        setNotFound(!found);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [taskId, child, profilesLoading]);

  function togglePlanned() {
    const next = !planned;
    setPlanned(next);
    try {
      if (next) localStorage.setItem(plannedKey(childId, taskId), "1");
      else localStorage.removeItem(plannedKey(childId, taskId));
    } catch {
      /* private nudge only — safe to ignore storage failures */
    }
  }

  const childName = child ? displayNameFor(child) : "";

  if (loading || profilesLoading) {
    return (
      <main className="container td-center">
        <Spinner size={30} label="Loading activity" />
      </main>
    );
  }

  if (notFound || !task) {
    return (
      <main className="container">
        <PageHeader title="Activity" back={`/grown-ups/child/${childId}`} />
        <EmptyState
          icon="🧭"
          title="We couldn't find that activity"
          hint="It may have moved or be resting for now. Head back and pick another."
          action={<Button variant="secondary" to={`/grown-ups/child/${childId}`}>Back to activities</Button>}
        />
      </main>
    );
  }

  const setting = SETTING_META[task.setting] || null;
  const materials = Array.isArray(task.materials) ? task.materials : [];
  const instructions = Array.isArray(task.parentInstructions) ? task.parentInstructions : [];
  const rubric = Array.isArray(task.rubric) ? task.rubric : [];

  return (
    <main className="container td-root">
      <PageHeader
        title={task.title || "Activity"}
        subtitle={childName ? `A little maths adventure for ${childName}` : undefined}
        back={`/grown-ups/child/${childId}`}
      >
        {planned && <Chip tone="ok">📌 Planned</Chip>}
      </PageHeader>

      {/* ── hook + meta + materials ─────────────────────────────────────────── */}
      <Card className="td-hero">
        {task.hook && <p className="td-hook">{task.hook}</p>}
        <div className="td-meta">
          {task.strand && <StrandBadge strand={task.strand} />}
          {Number.isFinite(task.estimatedMinutes) && (
            <Chip tone="neutral">
              <span aria-hidden="true">⏱️</span> about {task.estimatedMinutes} min
            </Chip>
          )}
          {setting && (
            <Chip tone="sky">
              <span aria-hidden="true">{setting.emoji}</span> {setting.label}
            </Chip>
          )}
          {task.repeatable && (
            <Chip tone="neutral">
              <span aria-hidden="true">🔁</span> Do it again anytime
            </Chip>
          )}
        </div>

        <div className="td-materials">
          <h2 className="td-h">What you'll need</h2>
          {materials.length ? (
            <ul className="td-materials__list">
              {materials.map((m, i) => (
                <li key={i} className="td-material">
                  <span className="td-material__item">{m.item}</span>
                  {m.optional && <Chip tone="neutral">optional</Chip>}
                  {m.substitute && <span className="td-material__sub">or use {m.substitute}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="td-plain">Nothing special — just the two of you.</p>
          )}
        </div>
      </Card>

      {/* ── why this matters + curriculum ───────────────────────────────────── */}
      <Card className="td-why">
        <h2 className="td-h">Why this matters</h2>
        {task.whyThisMatters && <p className="td-body">{task.whyThisMatters}</p>}
        <CurriculumTag codes={task.descriptorCodes} label="What it explores" />
      </Card>

      {/* ── parent instructions (numbered) ──────────────────────────────────── */}
      {instructions.length > 0 && (
        <Card className="td-steps">
          <h2 className="td-h">How to run it together</h2>
          <ol className="td-steplist">
            {instructions.map((step, i) => (
              <li key={i} className="td-step">
                <span className="td-step__n" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="td-step__text">{step}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {/* ── kid prompt card (distinct callout) ──────────────────────────────── */}
      {task.kidPromptCard && (
        <section className="td-kidcard" aria-label="Say this together">
          <span className="td-kidcard__label">
            <span aria-hidden="true">💬</span> Say this together
          </span>
          <p className="td-kidcard__text">{task.kidPromptCard}</p>
        </section>
      )}

      {/* ── rubric preview ──────────────────────────────────────────────────── */}
      {rubric.length > 0 && (
        <Card className="td-rubric">
          <h2 className="td-h">Little wins to notice</h2>
          <p className="td-sub">You'll tick any of these together afterwards — there's no wrong answer.</p>
          <ul className="td-rubric__list">
            {rubric.map((r) => (
              <li key={r.id}>
                <span className="td-rubric__dot" aria-hidden="true">
                  ○
                </span>
                {r.label}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── safety note ─────────────────────────────────────────────────────── */}
      {task.safetyNote && (
        <Note tone="warn">
          <strong>Safety:</strong> {task.safetyNote}
        </Note>
      )}

      {/* ── actions OR coming-soon ──────────────────────────────────────────── */}
      {task.comingSoon ? (
        <Card className="td-soon">
          <h2 className="td-h">Coming next term</h2>
          <p className="td-body">
            We're still polishing this one. It'll turn up right here, ready to go, when it's set. Thanks for
            your patience.
          </p>
        </Card>
      ) : (
        <div className="td-actions" role="group" aria-label="Activity actions">
          <Button variant="secondary" size="lg" to={`${base}/print`}>
            <span aria-hidden="true">🖨️</span> Print sheet
          </Button>
          <Button variant={planned ? "ghost" : "secondary"} size="lg" onClick={togglePlanned}>
            {planned ? "Unmark planned" : "Mark planned"}
          </Button>
          <Button variant="primary" size="lg" className="td-actions__go" to={`${base}/complete`}>
            <span aria-hidden="true">✅</span> We did it
          </Button>
        </div>
      )}
    </main>
  );
}

const TD_CSS = `
.td-center{display:grid;place-items:center;min-height:60vh;}
.td-root{display:flex;flex-direction:column;gap:1rem;padding-bottom:2.5rem;}
.td-h{margin:0 0 .5rem;font-size:1.05rem;}
.td-body{margin:0 0 .75rem;color:var(--ink);}
.td-sub{margin:.1rem 0 .7rem;color:var(--ink-soft);font-size:.92rem;}
.td-plain{margin:0;color:var(--ink-soft);}

.td-hook{margin:0 0 .9rem;font-size:1.15rem;font-weight:600;color:var(--brand-ink);line-height:1.35;}
.td-meta{display:flex;flex-wrap:wrap;gap:.5rem;margin-bottom:1rem;}

.td-materials__list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.5rem;}
.td-material{display:flex;flex-wrap:wrap;align-items:center;gap:.5rem;}
.td-material__item{font-weight:600;}
.td-material__sub{color:var(--ink-soft);font-size:.9rem;}

.td-steplist{list-style:none;margin:0;padding:0;counter-reset:step;display:flex;flex-direction:column;gap:.7rem;}
.td-step{display:flex;gap:.75rem;align-items:flex-start;}
.td-step__n{flex:0 0 auto;display:inline-grid;place-items:center;width:30px;height:30px;border-radius:50%;
  background:color-mix(in srgb,var(--brand) 14%,#fff);color:var(--brand-ink);font-weight:800;font-size:.95rem;}
.td-step__text{padding-top:.2rem;line-height:1.4;}

.td-kidcard{background:color-mix(in srgb,var(--accent) 12%,#fff);
  border:2px solid color-mix(in srgb,var(--accent) 40%,#fff);border-radius:var(--radius);
  padding:1.1rem 1.25rem;box-shadow:var(--shadow);}
.td-kidcard__label{display:inline-flex;align-items:center;gap:.4rem;font-size:.78rem;font-weight:700;
  text-transform:uppercase;letter-spacing:.05em;color:#7a4a10;}
.td-kidcard__text{margin:.5rem 0 0;font-size:1.3rem;font-weight:700;color:var(--brand-ink);line-height:1.3;}

.td-rubric__list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.4rem;}
.td-rubric__list li{display:flex;gap:.55rem;align-items:baseline;color:var(--ink);}
.td-rubric__dot{color:color-mix(in srgb,var(--brand) 55%,#fff);font-weight:700;}

.td-soon{background:color-mix(in srgb,var(--sky) 8%,#fff);border-color:color-mix(in srgb,var(--sky) 26%,#fff);}

.td-actions{display:flex;flex-wrap:wrap;gap:.6rem;margin-top:.5rem;}
.td-actions .ui-btn{flex:1 1 auto;}
.td-actions__go{flex:1 1 100%;order:-1;}
@media (min-width:560px){.td-actions__go{order:0;flex:1 1 auto;}}
`;

injectStyles("maths-taskdetail-styles", TD_CSS);
